import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  CircularProgress,
  Alert,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import httpclient from "../../../Utils"; 
import { useNotification } from "../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../Hooks/useApiErrorHandler";
import useTokenRefresh from "../../../Hooks/useTokenRefresh";

// Import TimePicker components
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopTimePicker } from "@mui/x-date-pickers/DesktopTimePicker";

const AutoAssignPopup = ({ open, onClose, rowData, onUpdateSuccess, isChangingToNo = false }) => {
  const [selectedTime, setSelectedTime] = useState(new Date());
  const [priority, setPriority] = useState("");
  const [bay, setBay] = useState("");
  const [priorityError, setPriorityError] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [action, setAction] = useState(isChangingToNo ? "disable" : "enable");

  // Hooks
  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();
  const { getTokenRefreshed: refresh } = useTokenRefresh();

  // Convert time string to Date object
  const convertToDate = (timeStr) => {
    const date = new Date();
    
    if (timeStr && typeof timeStr === 'string') {
      if (timeStr.includes(':')) {
        const [hours, minutes] = timeStr.split(':').map(Number);
        if (!isNaN(hours) && !isNaN(minutes)) {
          date.setHours(hours, minutes, 0, 0);
          return date;
        }
      } else {
        const hour = parseInt(timeStr, 10);
        if (!isNaN(hour)) {
          date.setHours(hour, 0, 0, 0);
          return date;
        }
      }
    }
    
    date.setHours(12, 0, 0, 0);
    return date;
  };

  // Convert Date object to "HH:MM" string
  const formatTimeForPayload = (date) => {
    if (!date) return null;
    
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${hours}:${minutes}`;
  };

  // Initialize values from rowData when popup opens
  useEffect(() => {
    if (open && rowData) {
      setBay(rowData?.bay || "");
      setPriority(rowData?.order_push_sequence_priority?.toString() || "");
      setAction(isChangingToNo ? "disable" : "enable");
      
      // Set time value from row data
      if (rowData?.order_push_sequence_time) {
        const timeValue = rowData.order_push_sequence_time;
        
        if (typeof timeValue === 'string') {
          setSelectedTime(convertToDate(timeValue));
        } else if (typeof timeValue === 'number') {
          setSelectedTime(convertToDate(`${timeValue}:00`));
        } else if (timeValue instanceof Date) {
          setSelectedTime(timeValue);
        }
      }
      
      // Clear any previous errors and states
      setPriorityError("");
      setError(null);
      setLoading(false);
    }
  }, [open, rowData, isChangingToNo]);

  const handleEnable = async () => {
    try {
      setLoading(true);
      setError(null);

      // Prepare payload for enabling
      const payload = {
        is_auto_assigned: 1
      };
      
      // Add time if provided
      const formattedTime = formatTimeForPayload(selectedTime);
      if (formattedTime) {
        payload.order_push_sequence_time = formattedTime;
      }
      
      // Add priority if provided
      if (priority) {
        payload.order_push_sequence_priority = parseInt(priority);
      }
      
      const res = await httpclient.post(
        `/bin-bay/update/${rowData.id}`,
        payload
      );

      const { data } = res;

      if (data.status === 200) {
        showNotification("success", data.message);
        
        if (onUpdateSuccess) {
          onUpdateSuccess();
        }
        
        onClose();
      } else {
        const errorMsg = data.message || "Failed to update auto assign";
        setError(errorMsg);
        showNotification("error", errorMsg);
      }
    } catch (error) {
      console.error("Update error:", error);
      
      if (error.response?.status === 401) {
        try {
          await refresh();
          await handleEnable();
          return;
        } catch (refreshError) {
          setError("Session expired. Please login again.");
          showNotification("error", "Session expired. Please login again.");
        }
      } else {
        handleApiError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    try {
      setLoading(true);
      setError(null);

      // Simple payload for disabling
      const payload = {
        is_auto_assigned: 0
      };
      
      const res = await httpclient.post(
        `/bin-bay/update/${rowData.id}`,
        payload
      );

      const { data } = res;

      if (data.status === 200) {
        showNotification("success", data.message);
        
        if (onUpdateSuccess) {
          onUpdateSuccess();
        }
        
        onClose();
      } else {
        const errorMsg = data.message || "Failed to update auto assign";
        setError(errorMsg);
        showNotification("error", errorMsg);
      }
    } catch (error) {
      console.error("Update error:", error);
      
      if (error.response?.status === 401) {
        try {
          await refresh();
          await handleDisable();
          return;
        } catch (refreshError) {
          setError("Session expired. Please login again.");
          showNotification("error", "Session expired. Please login again.");
        }
      } else {
        handleApiError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTimeChange = (newTime) => {
    if (newTime) {
      setSelectedTime(newTime);
    }
  };

  const handlePriorityChange = (event) => {
    const value = event.target.value;
    
    if (priorityError) {
      setPriorityError("");
    }
    
    if (value === "" || /^\d+$/.test(value)) {
      setPriority(value);
      
      if (value !== "" && parseInt(value) <= 0) {
        setPriorityError("Priority must be a positive integer");
      }
    }
  };

  // For disable action - Simple confirmation dialog
  if (action === "disable") {
    return (
      <Dialog
        open={open}
        onClose={loading ? undefined : onClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 24px",
            borderBottom: "1px solid #E0E0E0",
          }}
        >
          Disable Auto Assign
          <IconButton
            onClick={loading ? undefined : onClose}
            sx={{ padding: 0 }}
            disabled={loading}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: "24px" }}>
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}
          
          <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
            Are you sure you want to disable auto assign for bay <strong>{bay}</strong>?
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            padding: "16px 24px",
            borderTop: "1px solid #E0E0E0",
            gap: 2,
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            disabled={loading}
            sx={{
              borderRadius: "8px",
              padding: "8px 24px",
              borderColor: "#E0E0E0",
              color: "#333",
              textTransform: "none",
            }}
          >
            No
          </Button>
          <Button
            onClick={handleDisable}
            variant="contained"
            disabled={loading}
            sx={{
              borderRadius: "8px",
              padding: "8px 24px",
              backgroundColor: "#1A237E", // Changed to #1A237E
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#0D1642", // Darker shade for hover
              },
              minWidth: "120px",
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Yes"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // For enable action - Form with fields
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 24px",
          borderBottom: "1px solid #E0E0E0",
        }}
      >
        Enable Auto Assign
        <IconButton
          onClick={loading ? undefined : onClose}
          sx={{ padding: 0 }}
          disabled={loading}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "24px" }}>
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}
        
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, marginTop: "10px" }}>
            {/* Bay Display */}
            <TextField
              label="Bay"
              variant="outlined"
              value={bay}
              fullWidth
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly: true }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#f5f5f5",
                },
              }}
            />

            {/* Order Push Sequence Priority */}
            <TextField
              label="Order Push Sequence"
              variant="outlined"
              value={priority}
              onChange={handlePriorityChange}
              fullWidth
              error={!!priorityError}
              disabled={loading}
              helperText={priorityError}
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },
              }}
              InputProps={{
                inputProps: {
                  type: "number",
                  min: "1",
                  step: "1",
                }
              }}
            />
          </Box>
        </LocalizationProvider>
      </DialogContent>

      <DialogActions
        sx={{
          padding: "16px 24px",
          borderTop: "1px solid #E0E0E0",
          gap: 2,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={loading}
          sx={{
            borderRadius: "8px",
            padding: "8px 24px",
            borderColor: "#E0E0E0",
            color: "#333",
            textTransform: "none",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleEnable}
          variant="contained"
          disabled={loading}
          sx={{
            borderRadius: "8px",
            padding: "8px 24px",
            backgroundColor: "#1976d2",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#1565c0",
            },
            minWidth: "120px",
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Enable"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutoAssignPopup;